import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import {Inject, Injectable, Injector, inject} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {PaginatedResponse} from '@gatewee/types/system';
import {TwoFactorAuthService} from './two-factor-auth.service';
import {SKIP_SPINNER} from './tokens/spinner.token';

export type FilterOptions = {[key: string]: unknown};

@Injectable({
  providedIn: 'root',
})
export class GenericCrudService<T> {
  twoFactorAuthService = inject(TwoFactorAuthService);
  private readonly injector = inject(Injector);
  scope = this.injector.get('SCOPE') as 'recipient' | 'domain' | 'admin';

  constructor(
    @Inject('BASE_URL') public baseUrl: string,
    public http: HttpClient,
  ) {}

  createWith2Fa(data: T, skipSpinner = false): Observable<T> {
    return new Observable<T>(observer => {
      this.request2Fa(this.baseUrl, data)
        .then(() =>
          this.twoFactorAuthService.submit(
            this.baseUrl,
            data as Object,
            (code: string) =>
              this.create(
                {...(data as object), twoFactorCode: code} as T,
                skipSpinner,
              ),
          ),
        )
        .then((result: T) => {
          observer.next(result);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  request2Fa(_path: string, data: T, skipSpinner = false): Promise<void> {
    // Make a request to initiate 2FA process
    return new Promise<void>((resolve, reject) => {
      // Send request to initiate 2FA (this might trigger SMS/email)
      this.http
        .post(`/auth/${this.scope}/request-2fa?route=/v1${_path}`, data, {
          context: new HttpContext().set(SKIP_SPINNER, skipSpinner),
        })
        .subscribe({
          next: () => {
            console.log('2FA request initiated successfully');
            resolve();
          },
          error: error => {
            console.error('Failed to initiate 2FA:', error);
            reject(error);
          },
        });
    });
  }

  create(data: T, skipSpinner = false): Observable<T> {
    const twoFactorCode = (data as {twoFactorCode: string}).twoFactorCode;
    let params = new HttpParams();
    if (twoFactorCode) {
      delete (data as {twoFactorCode?: string}).twoFactorCode;
      params = params.set('twoFactorCode', twoFactorCode);
    }
    return this.http
      .post<T>(this.baseUrl, data, {
        params,
        context: new HttpContext().set(SKIP_SPINNER, skipSpinner),
      })
      .pipe();
  }

  // updateWith2Fa(id: number, data: Partial<T>): Observable<T> {
  //   // Return an observable that follows the 2FA flow for updates
  //   return new Observable<T>(observer => {
  //     // Step 1: Request 2FA
  //     this.request2Fa(`${this.baseUrl}/${id}`, data as T)
  //       .then(() => {
  //         // Step 2: Submit to twoFactorAuthService (show modal)
  //         return this.twoFactorAuthService.submit(
  //           `${this.baseUrl}/${id}`,
  //           data as Object,
  //         );
  //       })
  //       .then((twoFactorCode: string) => {
  //         // Step 3: Trigger the update function with 2FA code
  //         const dataWith2FA = {
  //           ...data,
  //           twoFactorCode: twoFactorCode,
  //         };

  //         // Use the existing update method
  //         this.update(id, dataWith2FA).subscribe({
  //           next: result => observer.next(result),
  //           error: error => observer.error(error),
  //           complete: () => observer.complete(),
  //         });
  //       })
  //       .catch(error => {
  //         observer.error(error);
  //       });
  //   });
  // }

  // removeWith2Fa(id: number): Observable<void> {
  //   // Return an observable that follows the 2FA flow for deletion
  //   return new Observable<void>(observer => {
  //     // Step 1: Request 2FA
  //     this.request2Fa(`${this.baseUrl}/${id}`, {id} as T)
  //       .then(() => {
  //         // Step 2: Submit to twoFactorAuthService (show modal)
  //         return this.twoFactorAuthService.submit(`${this.baseUrl}/${id}`, {
  //           id,
  //         });
  //       })
  //       .then((twoFactorCode: string) => {
  //         // Step 3: Trigger the remove function with 2FA code as query param
  //         const params = new HttpParams().set('twoFactorCode', twoFactorCode);

  //         // Use HTTP delete with 2FA code as query parameter
  //         this.http.delete<void>(`${this.baseUrl}/${id}`, {params}).subscribe({
  //           next: result => observer.next(result),
  //           error: error => observer.error(error),
  //           complete: () => observer.complete(),
  //         });
  //       })
  //       .catch(error => {
  //         observer.error(error);
  //       });
  //   });
  // }

  findAll(filters?: FilterOptions, skipSpinner = false): Observable<T[]> {
    let params = new HttpParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          value =
            typeof value === 'object'
              ? JSON.stringify(value)
              : value.toString();

          params = params.append(key, value as string);
        }
      });
    }

    return this.http
      .get<T[]>(this.baseUrl, {
        params,
        context: new HttpContext().set(SKIP_SPINNER, skipSpinner),
      })
      .pipe();
  }

  findAllPaginated(
    from: number,
    take: number,
    filters?: FilterOptions,
    skipSpinner = false,
  ): Observable<PaginatedResponse<T>> {
    let params = new HttpParams()
      .set('skip', from.toString())
      .set('take', take.toString());

    if (filters) {
      const reservedPaginationKeys = new Set(['page', 'take', 'skip']);
      Object.entries(filters).forEach(([key, value]) => {
        if (reservedPaginationKeys.has(key)) return;
        if (value !== null && value !== undefined) {
          value =
            typeof value === 'object'
              ? JSON.stringify(value)
              : value.toString();

          params = params.append(key, value as string);
        }
      });
    }

    return this.http
      .get<PaginatedResponse<T>>(`${this.baseUrl}/paginated`, {
        params,
        context: new HttpContext().set(SKIP_SPINNER, skipSpinner),
      })
      .pipe(catchError(error => this.handleError('findAllPaginated', error)));
  }

  download(id: number, skipSpinner = false): Observable<{url: string}> {
    return this.http.get<{url: string}>(`${this.baseUrl}/${id}/download`, {
      context: new HttpContext().set(SKIP_SPINNER, skipSpinner),
    });
  }

  findOne(
    id: number,
    filters?: FilterOptions,
    skipSpinner = false,
  ): Observable<T> {
    let params = new HttpParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          value =
            typeof value === 'object'
              ? JSON.stringify(value)
              : value.toString();

          params = params.append(key, value as string);
        }
      });
    }
    return this.http
      .get<T>(`${this.baseUrl}/${id}`, {
        params,
        context: new HttpContext().set(SKIP_SPINNER, skipSpinner),
      })
      .pipe();
  }

  update(id: number, data: Partial<T>, skipSpinner = false): Observable<T> {
    return this.http
      .patch<T>(`${this.baseUrl}/${id}`, data, {
        context: new HttpContext().set(SKIP_SPINNER, skipSpinner),
      })
      .pipe();
  }

  remove(id: number, skipSpinner = false): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}/${id}`, {
        context: new HttpContext().set(SKIP_SPINNER, skipSpinner),
      })
      .pipe();
  }

  disable(id: number, skipSpinner = false): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}/disable/${id}`, {
        context: new HttpContext().set(SKIP_SPINNER, skipSpinner),
      })
      .pipe();
  }

  /** Serialize list/filter options into HttpParams (same rules as findAll). */
  protected toFilterParams(
    filters?: FilterOptions,
    skipKeys: Iterable<string> = [],
  ): HttpParams {
    const skip = new Set(skipKeys);
    let params = new HttpParams();

    if (!filters) {
      return params;
    }

    for (const [key, value] of Object.entries(filters)) {
      if (skip.has(key) || value === null || value === undefined) {
        continue;
      }

      const serialized =
        typeof value === 'object' ? JSON.stringify(value) : String(value);

      params = params.append(key, serialized);
    }

    return params;
  }

  private handleError(
    operation: string,
    error: HttpErrorResponse,
  ): Observable<never> {
    console.error(`Error during operation '${operation}':`, error);
    return throwError(
      () =>
        new Error(`Error during operation '${operation}': ${error.message}`),
    );
  }

  summary(filters?: FilterOptions, skipSpinner = false) {
    let params = new HttpParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          value =
            typeof value === 'object'
              ? JSON.stringify(value)
              : value.toString();

          params = params.append(key, value as string);
        }
      });
    }

    return this.http
      .get<unknown>(this.baseUrl + '/summary', {
        params,
        context: new HttpContext().set(SKIP_SPINNER, skipSpinner),
      })
      .pipe();
  }
}
